import { useSelector } from 'react-redux';
import { selectUserPermissions, selectCurrentUser } from '../slices/userManagementSlice';

/**
 * Hook to check user permissions
 * Usage:
 * const { can, hasRole, isCategory } = usePermissions();
 *
 * if (can('canEditUsers')) {
 *   // Show edit button
 * }
 *
 * if (hasRole('admin')) {
 *   // Show admin features
 * }
 *
 * if (isCategory('platform_user')) {
 *   // Show platform user features
 * }
 */
export const usePermissions = () => {
  const permissions = useSelector(selectUserPermissions);
  const currentUser = useSelector(selectCurrentUser);

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission key to check
   * @returns {boolean}
   */
  const can = (permission) => {
    return permissions?.[permission] || false;
  };

  /**
   * Check if user has a specific role
   * @param {string} role - Role to check
   * @returns {boolean}
   */
  const hasRole = (role) => {
    return currentUser?.role === role;
  };

  /**
   * Check if user belongs to a specific category
   * @param {string} category - Category to check
   * @returns {boolean}
   */
  const isCategory = (category) => {
    return currentUser?.category === category;
  };

  /**
   * Check if user can perform multiple actions (AND logic)
   * @param {string[]} permissionList - Array of permissions to check
   * @returns {boolean}
   */
  const canAll = (...permissionList) => {
    return permissionList.every(permission => can(permission));
  };

  /**
   * Check if user can perform any of the actions (OR logic)
   * @param {string[]} permissionList - Array of permissions to check
   * @returns {boolean}
   */
  const canAny = (...permissionList) => {
    return permissionList.some(permission => can(permission));
  };

  return {
    can,
    canAll,
    canAny,
    hasRole,
    isCategory,
    currentUser,
    permissions
  };
};

export default usePermissions;
